/**
 * Settings the admin site needs at run time. A deployed site loads them from
 * /config.json, written per stage by the infrastructure (infra/), so one build
 * serves staging and prod. Local dev and tests use the VITE_* variables.
 * An empty value means "not available in this stage" (e.g. the store in
 * staging). Keys this build doesn't know (such as the removed chatApiUrl and
 * agentRuntime* of the old AI assistant) are ignored.
 */
export interface RuntimeConfig {
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  /** Admin API base URL, without a trailing slash. */
  adminApiUrl: string;
  /** Store API base URL; empty when the stage has no store. */
  storeApiUrl: string;
  /** Bucket for admin uploads (Amplify Storage). */
  mediaBucket: string;
  googleMapsApiKey: string;
}

const KEYS: (keyof RuntimeConfig)[] = [
  'region',
  'userPoolId',
  'userPoolClientId',
  'adminApiUrl',
  'storeApiUrl',
  'mediaBucket',
  'googleMapsApiKey',
];

const trimSlash = (url: string) => url.replace(/\/+$/, '');

let loaded: Partial<RuntimeConfig> | undefined;

function fromEnv(): RuntimeConfig {
  const env = import.meta.env;
  return {
    region: env.VITE_AWS_REGION || 'ap-southeast-2',
    userPoolId: env.VITE_USER_POOL_ID || '',
    userPoolClientId: env.VITE_USER_POOL_CLIENT_ID || '',
    adminApiUrl: trimSlash(env.VITE_ADMIN_API_ENDPOINT || ''),
    storeApiUrl: trimSlash(env.VITE_STORE_API_ENDPOINT || ''),
    mediaBucket: env.VITE_S3_BUCKET || '',
    googleMapsApiKey: env.VITE_GOOGLE_MAPS_API_KEY || '',
  };
}

/**
 * The current settings. With a /config.json, its values replace the VITE_*
 * ones entirely (an empty value there means "off in this stage").
 */
export function runtimeConfig(): RuntimeConfig {
  return loaded ? { ...fromEnv(), ...loaded } : fromEnv();
}

/** Load /config.json once at startup; without one (local dev), the VITE_* values stay. */
export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  try {
    const res = await fetchImpl('/config.json', { cache: 'no-store' });
    if (res.ok) {
      const body: unknown = await res.json();
      if (typeof body === 'object' && body !== null) {
        const next: Partial<RuntimeConfig> = {};
        for (const key of KEYS) {
          const value = (body as Record<string, unknown>)[key];
          if (typeof value === 'string') next[key] = value.trim();
        }
        for (const key of ['adminApiUrl', 'storeApiUrl'] as const) {
          if (next[key]) next[key] = trimSlash(next[key]!);
        }
        loaded = next;
      }
    }
  } catch {
    // No config.json (the dev server answers with index.html): keep the VITE_* values.
  }
  return runtimeConfig();
}

/** Tests: forget a loaded /config.json. */
export function resetRuntimeConfig(): void {
  loaded = undefined;
}
